<template>
  <div :class="position">
    <div v-if="reUpload || (!path && !justUploaded)"
         :data-active="active"
         @dragenter.prevent="setActive"
         @dragover.prevent="setActive"
         @dragleave.prevent="setInactive"
         @drop.prevent="onDrop"
         @click="triggerClickInput"
         class="relative flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed p-8 group"
         :style="dragZoneStyles"
         :class="dragZoneClasses">
      <slot>
        <img
          :src="mimeImage.path"
          :alt="mimeImage.alt"
          :width="mimeImage.width"
          :height="mimeImage.height"
          class="h-20 w-auto"
        />
        <p class="mt-2 text-center">
          Drag and drop or
          <u class="group-hover:text-primary-600">browse</u> • Up to {{ formatFilesize(maxFileSizeBytes) }}
        </p>
      </slot>
      <input type="file" :accept="accept" ref="fileInput" hidden @change="onFileChange" @click="resetEventTarget"/>
    </div>
    <div v-else-if="allowNonImages"
         class="relative flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white p-4 group">
      <Component :is="fileMimeIcon" class="mx-auto h-6 w-6 text-red-600"/>
      <p class="font-bold text-slate-600">{{ file?.name || "File Uploaded" }}</p>
      <p v-if="file?.size" class="text-slate-400">{{ formatFilesize(file?.size) }}</p>
    </div>
    <div v-else-if="isPdfMime">
      <embed
        v-if="path"
        :src="path"
        type="application/pdf"
        title="PDF Preview"
        width="100%"
        height="375"
      >
    </div>
    <div v-else :style="aspectRatio && ('aspect-ratio:' + aspectRatio)" :class="!hideBorder && 'border'"
         class="relative mx-auto flex h-full max-h-full w-full items-center justify-start gap-6 overflow-hidden border-slate-200 bg-slate-100">
      <img v-if="path" :src="path" alt="" :class="imageClasses" loading="lazy"/>
    </div>
    <div v-if="allowDelete && (path || justUploaded)" @click="confirmDelete=true"
         class="absolute -bottom-8 left-0 z-10 flex cursor-pointer items-center gap-2 text-slate-400 hover:underline">
      <TrashIcon class="h-4 w-4"/>
      Delete
    </div>
    <ModalConfirm v-model:open="confirmDelete" level="danger"
                  @confirm="deleteFile"
                  title="Are you sure you want to permanently delete this file?"
                  description="Once the change is saved, there will be no way to recover it."
                  proceed-button-text="Delete"/>
    <div
      v-if="path"
      @click="handleReUpload"
      class="absolute right-0 -bottom-8 z-10 flex cursor-pointer items-center gap-2 text-slate-400 hover:underline">
      <ArrowsRightLeftIcon class="h-4 w-4"/>
      Change
    </div>
    <UtilityCropper
      v-model:open="showCropper"
      :file="file"
      :image="path"
      disable-cancel
      @update:file="updateFile"
      @update:image="updatePath"
    />
  </div>
</template>

<script setup>
import {ArrowsRightLeftIcon, DocumentIcon, PhotoIcon, TrashIcon} from "@heroicons/vue/24/outline";
import {computed, onMounted, onUnmounted, ref} from "vue";
import {formatFilesize} from "../helpers/formatters.js";
import ModalConfirm from "./ModalConfirm.vue";
import UtilityCropper from "./UtilityCropper.vue";
import {getMimeImage} from "../constants/MimeImages.js";

const IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/gif", "image/webp"];

const EVENTS_TO_HANDLE = ["dragenter", "dragover", "dragleave", "drop"];

const props = defineProps({
  name: String,
  path: String,
  file: Object,
  autofocus: Boolean,
  dragZoneClasses: String,
  aspectRatio: Number,
  allowNonImages: Boolean,
  hideBorder: Boolean,
  allowDelete: Boolean,
  allowCrop: Boolean,
  dragZoneColorClasses: {
    type: String,
    default: "border-slate-300 bg-white",
  },
  getDragZoneColorStyles: Function,
  accept: {
    type: String,
    default: "image/png, image/jpeg, image/jpg, 'image/svg+xml', image/gif, image/webp",
  },
  position: {
    type: String,
    default: "relative",
  },
  imageClasses: {
    type: String,
    default: "max-h-full max-w-full w-full p-6 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 object-contain",
  },
  maxFileSizeBytes: {
    type: Number,
    default: 4 * 1024 * 1024,
  },
});

const emits = defineEmits([
  "update:modelValue", // Both file & path in one
  "update:file",
  "update:path",
  "delete",
  "unmounted",
]);

const fileInput = ref(null);
const active = ref(false);
const internalBase64 = ref(null);
const reUpload = ref(false);
const justUploaded = ref(false);
const confirmDelete = ref(false);
const showCropper = ref(false);
let inActiveTimeout = null;

const setActive = () => {
  active.value = true;
  clearTimeout(inActiveTimeout);
};

const setInactive = () => {
  inActiveTimeout = setTimeout(() => {
    active.value = false;
  }, 50);
};

const enforceMaxSize = (file) => {
  if (file.size > props.maxFileSizeBytes) {
    toast.info("File size exceeds the maximum allowed size.");
    return false;
  }
  return true;
};

const deleteFile = () => {
  emits("delete");
  emits("update:file", null);
  emits("update:path", null);
};

const onFileChange = (e) => {
  let file = e.target.files[0];
  if (isAllowedMimeType(file.type)) handleValidMimeFile(file);
  else toast.info("Invalid file type. Please upload a valid file type.");
};

const onDrop = (e) => {
  let file = e.dataTransfer.files[0];
  if (isAllowedMimeType(file.type)) handleValidMimeFile(e.dataTransfer.files[0]);
  else setInactive();
};

const isAllowedMimeType = (mimeType) => {
  return props.accept.includes(mimeType);
};

const handleValidMimeFile = (file) => {
  setInactive();
  if (file) {
    if (!enforceMaxSize(file)) return;

    if (props.accept.includes(file.type)) internalBase64.value = URL.createObjectURL(file);

    emits("update:path", internalBase64.value || null);
    emits("update:file", file);
    emits("update:modelValue", {file: file, path: internalBase64.value});

    reUpload.value = false;
    justUploaded.value = true;
    showCropper.value = props.allowCrop;
  }
};

const resetEventTarget = (e) => e.target.value = "";
const triggerClickInput = () => fileInput.value.click();

const updateFile = (file) => {
  if (!enforceMaxSize(file)) return;
  emits("update:file", file);
  let newModelValue = {...props.modelValue, file: file};
  emits("update:modelValue", newModelValue);
};

const updatePath = (path) => {
  emits("update:path", path);
  let newModelValue = {...props.modelValue, path: path};
  emits("update:modelValue", newModelValue);
};

const handleReUpload = () => {
  reUpload.value = true;
  emits("update:file", null);
};

const dragZoneStyles = computed(() => {
  if (props.getDragZoneColorStyles) return props.getDragZoneColorStyles(active.value);
  if (active.value) return "border-color:#3472C6;background-color:#DFE9F7;";
  return "border-color: rgb(203 213 225);color:#64748b;";
});

const mimeImage = getMimeImage(props.accept);

const isPdfMime = computed(() => {
  if (props.file && props.file.type === "application/pdf") return true;
  return props.path && props.path.endsWith(".pdf");
});

const fileMimeIcon = computed(() => {
  if (props.file) {
    if (IMAGE_MIME_TYPES.includes(props.file.type)) return PhotoIcon;
    return DocumentIcon;
  }
  return null;
});

function preventDefaults(e) {
  e.preventDefault();
}

onMounted(() => {
  if (props.autofocus && fileInput.value) {
    try {
      fileInput.value.click();
    } catch (e) {
      console.error("Error while trying to focus file input", e);
    }
  }
  EVENTS_TO_HANDLE.forEach((eventName) => document.body.addEventListener(eventName, preventDefaults));
});

onUnmounted(() => {
  EVENTS_TO_HANDLE.forEach((eventName) => document.body.removeEventListener(eventName, preventDefaults));
  emits("unmounted");
});
</script>
